import React, { forwardRef, useImperativeHandle, useState } from 'react'

import { Botao, SelectUnform, Spacer, TextAreaUnform } from 'src/componentes'
import { ModalBase } from 'src/componentes/modal-base'
import { FormUnform } from 'src/componentes/unform/form'
import { Api } from 'src/servicos'
import {
  StatusCondicaoEspecialEditaveis,
  StatusCondicaoEspecialPorExtenso
} from 'src/tipos'

import { AcoesModal, Corpo } from './styles'

export interface ModalAlterarStatusRef {
  abrir: (statusAtual?: string) => void
  fechar: () => void
}

interface ModalAlterarStatusProps {
  backdrop?: boolean
  condicaoId: string
  aoAlterar: () => void
}

/**
 * Alteração manual de status pelo consultor.
 *
 * Só oferece os status que o domínio permite definir à mão — FinalizadaAluno,
 * EntradaPaga e Finalizada dependem de ação do aluno ou de pagamento
 * confirmado, e o backend recusa (PropostaMatricula.AlterarStatusManualmente).
 */
const Modal: React.ForwardRefRenderFunction<
  ModalAlterarStatusRef,
  ModalAlterarStatusProps
> = ({ backdrop, condicaoId, aoAlterar }, ref) => {
  const [aberta, definirAberta] = useState(false)
  const [carregando, definirCarregando] = useState(false)
  const [statusAtual, definirStatusAtual] = useState<string>(null)

  // Sem essa key o Unform retém valores de aberturas anteriores do modal.
  const [chaveFormulario, definirChaveFormulario] = useState(0)

  const fechar = () => definirAberta(false)

  const abrir = (status?: string) => {
    definirStatusAtual(status ?? null)
    definirChaveFormulario(chave => chave + 1)
    definirAberta(true)
  }

  useImperativeHandle<ModalAlterarStatusRef, ModalAlterarStatusRef>(
    ref,
    () => ({ abrir, fechar })
  )

  const opcoesStatus = StatusCondicaoEspecialEditaveis.filter(
    status => status !== statusAtual
  ).map(status => ({
    id: status,
    texto:
      StatusCondicaoEspecialPorExtenso[
        status as keyof typeof StatusCondicaoEspecialPorExtenso
      ]
  }))

  const acaoSucesso = async (dados: {
    status: string
    observacao: string
  }) => {
    if (!dados.status || !dados.observacao?.trim()) return

    try {
      definirCarregando(true)
      await Api.AlterarStatusCondicaoEspecial(
        condicaoId,
        dados.status,
        dados.observacao.trim()
      )
      fechar()
      aoAlterar()
    } catch {
      // Mensagens do backend já viram toast no interceptor.
    } finally {
      definirCarregando(false)
    }
  }

  return (
    <ModalBase aberta={aberta} backdrop={backdrop} id="modal-alterar-status">
      <Corpo>
        <h5>Alterar status</h5>
        <p>
          Os demais status são definidos pelo sistema conforme o aluno avança na
          matrícula.
        </p>
        <Spacer padding="10px 0px" />
        <FormUnform key={chaveFormulario} acaoSucesso={acaoSucesso}>
          <SelectUnform
            id="slct_novo_status"
            name="status"
            label="Novo status"
            placeholder="Selecione"
            obrigatorio
            opcoes={opcoesStatus}
          />
          <TextAreaUnform
            id="ipt_observacao_status"
            name="observacao"
            label="Motivo da alteração"
            obrigatorio
            maxLength={500}
          />
          <Spacer padding="10px 0px" />
          <AcoesModal>
            <Botao
              type="button"
              id="btn-cancelar-status"
              texto="Cancelar"
              tema="Secundario"
              disabled={carregando}
              onClick={fechar}
            />
            <Botao
              id="btn-salvar-status"
              texto="Salvar"
              type="submit"
              carregando={carregando}
            />
          </AcoesModal>
        </FormUnform>
      </Corpo>
    </ModalBase>
  )
}

export const ModalAlterarStatus = forwardRef(Modal)
